// app/helpers/interactions/handlersMouseMove.js
import {
  calculateAngle,
  calculatePolygonArea,
  calculatePolygonPerimeter,
} from "../geometry";
import {
  isPointNearPoint,
  isPointOnPolygonEdge,
  getTextBounds,
} from "../hitTest";
import { distUnits } from "./context";
import { computeDistance } from "./handlersMouseDown";

// handlersMouseMove.js

// local helper: canvas-space -> page-space for DOM positioning
function canvasToPage(pt, ctx) {
  const canvasEl =
    (ctx.fabricCanvasRef && ctx.fabricCanvasRef.current) ||
    (ctx.canvasRef && ctx.canvasRef.current);
  if (!canvasEl) return { x: 0, y: 0 };

  const rect = canvasEl.getBoundingClientRect();

  // canvas (logical) size
  const csW = Math.max(1, Number(ctx.canvasSize?.width ?? canvasEl.width ?? 1));
  const csH = Math.max(
    1,
    Number(ctx.canvasSize?.height ?? canvasEl.height ?? 1)
  );

  // display (CSS) size — use the actual rendered size to avoid any mismatch
  const dsW = rect.width;
  const dsH = rect.height;

  // start in canvas/image space
  let x = pt.x;
  let y = pt.y;

  // move origin to center
  const cx = csW / 2;
  const cy = csH / 2;
  x -= cx;
  y -= cy;

  // apply zoom & pan
  const z = Math.max(0.0001, Number(ctx.zoom ?? 1));
  x = x * z + (ctx.imageOffset?.x ?? 0);
  y = y * z + (ctx.imageOffset?.y ?? 0);

  // apply image transform position
  const pos = ctx.imageTransforms?.position ?? { x: 0, y: 0 };
  x += pos.x;
  y += pos.y;

  // apply rotation
  const rotDeg = Number(ctx.imageTransforms?.rotation ?? 0);
  const r = (rotDeg * Math.PI) / 180;
  const rx = x * Math.cos(r) - y * Math.sin(r);
  const ry = x * Math.sin(r) + y * Math.cos(r);

  // back to top-left origin
  x = rx + cx;
  y = ry + cy;

  // map canvas size -> display size
  const scaleX = dsW / csW;
  const scaleY = dsH / csH;
  const dispX = x * scaleX;
  const dispY = y * scaleY;

  // display -> page coords
  return { x: rect.left + dispX, y: rect.top + dispY };
}

export function makeHandleMouseMove(ctx) {
  return function handleMouseMove(event, fabricPoint) {
    const point = fabricPoint || ctx.getCanvasPoint(event);
    const canvas = ctx.fabricCanvasRef.current;
    if (!canvas) return;
    // Hand cursor for pan tool
    const el =
      canvas.upperCanvasEl || canvas.lowerCanvasEl || canvas.getElement?.();

    // Dragging a dot
    if (ctx.isDraggingDot && ctx.draggedDotId) {
      const off = ctx.dotDragOffset || { x: 0, y: 0 };
      const nx = point.x - off.x;
      const ny = point.y - off.y;
      ctx.setDots?.((prev) =>
        prev.map((d) =>
          d.id === ctx.draggedDotId ? { ...d, position: { x: nx, y: ny } } : d
        )
      );
      // no history push here; we’ll push on mouse up
      return;
    }
    // draging arrow
    if (ctx.isDraggingArrow && ctx.draggedArrowId) {
      const point = fabricPoint || ctx.getCanvasPoint(event);
      if (ctx.arrowDragMode === "start") {
        ctx.setArrows?.((prev) =>
          prev.map((a) =>
            a.id === ctx.draggedArrowId
              ? { ...a, start: { x: point.x, y: point.y } }
              : a
          )
        );
        return;
      }
      if (ctx.arrowDragMode === "end") {
        ctx.setArrows?.((prev) =>
          prev.map((a) =>
            a.id === ctx.draggedArrowId
              ? { ...a, end: { x: point.x, y: point.y } }
              : a
          )
        );
        return;
      }
      if (ctx.arrowDragMode === "body") {
        const a = ctx.arrows.find((x) => x.id === ctx.draggedArrowId);
        if (!a) return;
        const cx = point.x - (ctx.dragOffset?.x || 0);
        const cy = point.y - (ctx.dragOffset?.y || 0);
        const curCX = (a.start.x + a.end.x) / 2;
        const curCY = (a.start.y + a.end.y) / 2;
        const dx = cx - curCX;
        const dy = cy - curCY;
        ctx.setArrows?.((prev) =>
          prev.map((x) =>
            x.id !== a.id
              ? x
              : {
                  ...x,
                  start: { x: x.start.x + dx, y: x.start.y + dy },
                  end: { x: x.end.x + dx, y: x.end.y + dy },
                }
          )
        );
        return;
      }
    }

    // if pan tool selected → show "grab"; if actually panning → "grabbing"
    if (ctx.activeTool === "pan" || ctx.isPanning) {
      if (el) el.style.cursor = ctx.isPanning ? "grabbing" : "grab";
    } else {
      // reset so other tools can control cursor normally
      if (el) el.style.cursor = "";
    }

    if (ctx.isPanning) {
      return;
      canvas.selection = false; // ⚡ important
      const deltaX = point.x - ctx.lastPanPoint.x;
      const deltaY = point.y - ctx.lastPanPoint.y;
      const vpt = canvas.viewportTransform;
      vpt[4] += deltaX;
      vpt[5] += deltaY;
      canvas.renderAll(); // immediate redraw
      ctx.setLastPanPoint(point);
    }

    // Text resize
    if (ctx.isResizingText && ctx.textResizeHandle && ctx.selectedText) {
      ctx.setTextAnnotations((prev) =>
        prev.map((a) => {
          if (a.id !== ctx.selectedText) return a;
          const b = getTextBounds(a);
          let w = b.width,
            h = b.height,
            x = b.x,
            y = b.y;
          switch (ctx.textResizeHandle) {
            case "se":
              w = Math.max(50, point.x - b.x);
              h = Math.max(30, point.y - b.y);
              break;
            case "sw":
              w = Math.max(50, b.x + b.width - point.x);
              h = Math.max(30, point.y - b.y);
              x = point.x;
              break;
            case "ne":
              w = Math.max(50, point.x - b.x);
              h = Math.max(30, b.y + b.height - point.y);
              y = point.y;
              break;
            case "nw":
              w = Math.max(50, b.x + b.width - point.x);
              h = Math.max(30, b.y + b.height - point.y);
              x = point.x;
              y = point.y;
              break;
            case "e":
              w = Math.max(50, point.x - b.x);
              break;
            case "w":
              w = Math.max(50, b.x + b.width - point.x);
              x = point.x;
              break;
            case "s":
              h = Math.max(30, point.y - b.y);
              break;
            case "n":
              h = Math.max(30, b.y + b.height - point.y);
              y = point.y;
              break;
          }
          return { ...a, width: w, height: h, position: { x, y } };
        })
      );
      return;
    }

    // Text rotate
    if (ctx.isRotatingText && ctx.selectedText) {
      const a = ctx.textAnnotations.find((t) => t.id === ctx.selectedText);
      if (a) {
        const b = getTextBounds(a);
        const cx = b.x + b.width / 2;
        const cy = b.y + b.height / 2;
        const ang = Math.atan2(point.y - cy, point.x - cx) * (180 / Math.PI);
        ctx.setTextAnnotations((prev) =>
          prev.map((t) =>
            t.id === ctx.selectedText ? { ...t, rotation: ang } : t
          )
        );
      }
      return;
    }

    // Scale drag
    if (ctx.isDraggingScale) {
      ctx.setScalePosition({
        x: point.x - ctx.dragOffset.x,
        y: point.y - ctx.dragOffset.y,
      });
      return;
    }
    if (ctx.isDraggingScaleEnd) {
      const end = {
        x: point.x - ctx.dragOffset.x,
        y: point.y - ctx.dragOffset.y,
      };
      const length = Math.hypot(
        end.x - ctx.scalePosition.x,
        end.y - ctx.scalePosition.y
      );
      ctx.setScaleLength(Math.max(50, length));
      ctx.setScaleAngle(
        Math.atan2(end.y - ctx.scalePosition.y, end.x - ctx.scalePosition.x)
      );
      return;
    }
    // if (ctx.isDraggingScaleEnd) {
    //   const end = {
    //     x: point.x - ctx.dragOffset.x,
    //     y: point.y - ctx.dragOffset.y,
    //   };

    //   const dx = end.x - ctx.scalePosition.x;
    //   const dy = end.y - ctx.scalePosition.y;

    //   let angle = Math.atan2(dy, dx);
    //   const snapThreshold = 5 * (Math.PI / 180); // 5° tolerance

    //   // Cardinal directions: right (0°), up (90°), left (180°), down (-90°)
    //   const right = 0;
    //   const up = Math.PI / 2;
    //   const left = Math.PI;
    //   const down = -Math.PI / 2;

    //   const candidates = [right, up, left, down];
    //   let snapped = false;

    //   for (const cand of candidates) {
    //     if (Math.abs(angle - cand) < snapThreshold) {
    //       angle = cand;
    //       snapped = true;
    //       ctx.showVerticalHint = cand === up || cand === down;
    //       ctx.showHorizontalHint = cand === right || cand === left;
    //       break;
    //     }
    //   }

    //   const length = Math.hypot(dx, dy);
    //   ctx.setScaleLength(Math.max(50, length));
    //   ctx.setScaleAngle(angle);
    //   ctx.isScaleSnapped = snapped;

    //   return;
    // }

    // Text drag
    if (ctx.isDraggingText && ctx.draggedTextId) {
      ctx.setTextAnnotations((prev) =>
        prev.map((a) =>
          a.id === ctx.draggedTextId
            ? {
                ...a,
                position: {
                  x: point.x - ctx.dragOffset.x,
                  y: point.y - ctx.dragOffset.y,
                },
              }
            : a
        )
      );
      return;
    }

    // Polygon drag
    if (ctx.isDraggingPolygon && ctx.draggedPolygonId) {
      ctx.setPolygons((prev) =>
        prev.map((poly) => {
          if (poly.id !== ctx.draggedPolygonId) return poly;
          if (poly.isImport) return poly; // 🚫 block drag for imported
          if (ctx.draggedPointIndex !== null) {
            const pts = [...poly.points];
            pts[ctx.draggedPointIndex] = point;
            const area = calculatePolygonArea(
              pts,
              ctx.scale,
              ctx.scaleLength,
              ctx.fabricCanvasRef
            ); // px²
            const per = calculatePolygonPerimeter(pts, (a, b) =>
              distUnits(a, b, {
                scale: ctx.scale,
                scaleLength: ctx.scaleLength,
              })
            );
            return { ...poly, points: pts, area, perimeter: per };
          } else {
            const cx =
              poly.points.reduce((s, p) => s + p.x, 0) / poly.points.length;
            const cy =
              poly.points.reduce((s, p) => s + p.y, 0) / poly.points.length;
            const dx = point.x - ctx.dragOffset.x - cx;
            const dy = point.y - ctx.dragOffset.y - cy;
            const pts = poly.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
            return { ...poly, points: pts };
          }
        })
      );
      return;
    }

    // Dragging endpoints
    if (ctx.isDragging && ctx.dragPoint) {
      ctx.setMeasurements((prev) =>
        prev.map((m) => {
          if (m.id !== ctx.dragPoint.measurementId) return m;
          if ("start" in m) {
            const next = { ...m };
            if (ctx.dragPoint.pointType === "start") next.start = point;
            if (ctx.dragPoint.pointType === "end") next.end = point;
            next.distance = distUnits(next.start, next.end, {
              scale: ctx.scale,
              scaleLength: ctx.scaleLength,
            });
            return next;
          } else {
            const next = { ...m };

            if (ctx.dragPoint.pointType === "center") next.center = point;
            if (ctx.dragPoint.pointType === "point1") next.point1 = point;
            if (ctx.dragPoint.pointType === "point2") next.point2 = point;
            next.angle = calculateAngle(next.center, next.point1, next.point2);
            next.arm1Distance = computeDistance(next.center, next.point1, ctx);
            next.arm2Distance = computeDistance(next.center, next.point2, ctx);

            return next;
          }
        })
      );
      return;
    }

    // Drag whole line
    if (ctx.isDraggingLine && ctx.draggedLineId) {
      const m = ctx.measurements.find((mm) => mm.id === ctx.draggedLineId);
      if (m && "start" in m) {
        const cx = point.x - ctx.dragOffset.x;
        const cy = point.y - ctx.dragOffset.y;
        const curCX = (m.start.x + m.end.x) / 2;
        const curCY = (m.start.y + m.end.y) / 2;
        const dx = cx - curCX;
        const dy = cy - curCY;
        ctx.setMeasurements((prev) =>
          prev.map((mm) => {
            if (mm.id !== ctx.draggedLineId || !("start" in mm)) return mm;
            const start = { x: mm.start.x + dx, y: mm.start.y + dy };
            const end = { x: mm.end.x + dx, y: mm.end.y + dy };
            return {
              ...mm,
              start,
              end,
              distance: distUnits(start, end, {
                scale: ctx.scale,
                scaleLength: ctx.scaleLength,
              }),
            };
          })
        );
      }
      return;
    }

    // Guideline drag
    if (ctx.isDraggingGuideline && ctx.draggedGuidelineId) {
      ctx.setGuidelines((prev) =>
        prev.map((g) =>
          g.id === ctx.draggedGuidelineId
            ? { ...g, position: g.type === "vertical" ? point.x : point.y }
            : g
        )
      );
      return;
    }

    // Comment hover
    for (const c of ctx.comments) {
      if (
        ctx.activeTool === "comment" &&
        isPointNearPoint(point, c.position, 15)
      ) {
        ctx.onShowCommentTooltip(true, c, c.position);
        ctx.setHoveredPoint(point);
        return;
      }
    }
    ctx.onShowCommentTooltip(false);
    ctx.setHoveredPoint(point);
  };
}
